/**
 * Shared pricing engine. Pure functions over PricingData — no I/O.
 * Used by: SPA (live totals), Worker /checkout (authoritative reprice),
 * Shopify sync (variant prices + bulk_tiers metafield), intake validation.
 *
 * Bulk rule: when the summed quantity of a given SIZE (across finishes within its
 * product line) reaches the line's bulkThreshold, ALL prints of that size reprice
 * to bulkPrice. Fine art, specialty, and framed items have no bulk tier.
 *
 * Bulk fulfilment model (non-Plus): Shopify Function discounts need Shopify Plus,
 * so an ACTIVE bulk group is moved onto the size's dedicated bulk variant
 * (ctypeBulkSku, "RPLP-CT-4X6-B") whose variant price IS the bulkPrice — the
 * Worker /checkout variant lookup and the intake manifest inherit the switch
 * automatically. Below the threshold the group keeps the base SKU/price.
 *
 * Framing: a framed item is a print with `frameId` set — priced at the coupled
 * framed price (print INCLUDED), grouped into its own cart line by frameId.
 */

import type {
  BorderMode,
  BorderStyle,
  CartGroup,
  FramedProduct,
  PaperId,
  PaperTier,
  PricingData,
  PrintSelection,
  ProductLineId,
  ResolvedSize,
  Totals,
} from './types';
import {
  ctypeBulkSku,
  ctypeSizeId,
  customSizeInches,
  customSku,
  fineArtSizeId,
  frameId as makeFrameId,
  frameSku,
  groupKey,
  isSpecialtyPaper,
  parseFrameId,
  parseSizeId,
  sizeLabel,
  skuFor,
} from './ids';

/** Parse a numeric size string ("8x10", "5x7.5") into inches. */
export function sizeStringToInches(size: string): [number, number] {
  const [a, b] = size.split('x').map(Number);
  return [a, b];
}

/** Flatten pricing data into a sizeId-keyed catalog (bare prints only). */
export function buildCatalog(data: PricingData): Map<string, ResolvedSize> {
  const catalog = new Map<string, ResolvedSize>();
  for (const lineId of ['ctype_standard', 'ctype_enlargements'] as const) {
    const line = data.productLines[lineId];
    for (const s of line.sizes) {
      const sizeId = ctypeSizeId(s.size);
      catalog.set(sizeId, {
        sizeId,
        family: 'ctype',
        productLine: lineId,
        size: s.size,
        label: sizeLabel(s.size, s.inches),
        inches: s.inches,
        pano: !!s.pano,
        price: s.price,
        bulkPrice: s.bulkPrice,
        bulkThreshold: line.bulkThreshold,
      });
    }
  }
  const fa = data.productLines.fine_art;
  for (const s of fa.sizes) {
    const sizeId = fineArtSizeId(s.size);
    catalog.set(sizeId, {
      sizeId,
      family: 'fine_art',
      productLine: 'fine_art',
      size: s.size,
      label: sizeLabel(s.size, s.inches),
      inches: s.inches,
      pano: false,
      standard: s.standard,
      fineArt: s.fineArt,
    });
  }
  return catalog;
}

export function paperTierOf(data: PricingData, paper: PaperId): PaperTier {
  const info = data.papers[paper];
  if (!info) throw new Error(`Unknown paper: ${paper}`);
  return info.priceTier;
}

/** Formula group for custom sizing: Bauhaus (standard tier) vs everything premium. */
function customGroup(tier: PaperTier): 'standard' | 'fineArt' {
  return tier === 'standard' ? 'standard' : 'fineArt';
}

/**
 * Inside-fit floor: the dearest LISTED fine-art price whose size fits entirely
 * inside the custom rectangle (either orientation). A custom print physically
 * contains at least that much print, so it must never sell for less — without
 * this, entering a listed size (or a whisker over it) as a custom size was a
 * discount code: 13 of 21 standard and 6 of 21 fine-art listed sizes were
 * undercut by the area formula, A1 by $16/$14 (owner review 2026-08-27).
 * Genuine in-between sizes still price by area, sitting naturally between
 * their listed neighbours — that is the point of custom sizing and survives.
 */
function insideFitFloor(
  data: PricingData,
  wIn: number,
  hIn: number,
  group: 'standard' | 'fineArt',
): number {
  const EPS = 1e-9; // inches are snapped to 0.1; guard float noise only
  let floor = 0;
  for (const s of data.productLines.fine_art.sizes) {
    const [a, b] = s.inches;
    const fits =
      (a <= wIn + EPS && b <= hIn + EPS) || (a <= hIn + EPS && b <= wIn + EPS);
    if (fits && s[group] > floor) floor = s[group];
  }
  return floor;
}

/**
 * Custom fine-art size price: ceil(L_in × W_in × perSqIn[group]) in whole
 * dollars, floored at BOTH customSizing.minPrice[group] (owner 2026-08-24: a
 * custom 4×8 priced at $7 while the smallest listed fine-art size is $15/$19)
 * AND the inside-fit floor above (owner 2026-08-27). Specialty (Bizan) papers
 * are excluded from custom sizing.
 */
export function customPriceFor(data: PricingData, sizeId: string, paper: PaperId): number {
  const cs = data.customSizing;
  if (!cs?.enabled) throw new Error('Custom sizing is disabled');
  const tier = paperTierOf(data, paper);
  if (tier === 'ctype' || tier === 'specialty') {
    throw new Error(`Custom sizing is not available on ${paper}`);
  }
  const [w, h] = customSizeInches(sizeId);
  const [maxA, maxB] = cs.maxInches;
  const [lo, hi] = [Math.min(w, h), Math.max(w, h)];
  if (lo < cs.minInches || lo > Math.min(maxA, maxB) || hi > Math.max(maxA, maxB)) {
    throw new Error(`Custom size out of range: ${sizeId}`);
  }
  const group = customGroup(tier);
  const raw = w * h * cs.perSqIn[group];
  const price = cs.roundUpToDollar ? Math.ceil(raw - 1e-9) : Math.round(raw * 100) / 100;
  // Never sell a custom print below the smallest listed size of its tier, nor
  // below any listed size that fits inside it.
  const floor = Math.max(cs.minPrice?.[group] ?? 0, insideFitFloor(data, w, h, group));
  return price < floor ? floor : price;
}

/** Inches for any sizeId (catalog or custom). */
export function inchesFor(data: PricingData, sizeId: string): [number, number] {
  const parsed = parseSizeId(sizeId);
  if (parsed.custom) return customSizeInches(sizeId);
  const entry = buildCatalog(data).get(sizeId);
  if (!entry) throw new Error(`Unknown sizeId: ${sizeId}`);
  return entry.inches;
}

/** Base (non-bulk) unit price for a bare size+paper. */
export function priceFor(data: PricingData, sizeId: string, paper: PaperId): number {
  const parsed = parseSizeId(sizeId);
  if (parsed.custom) return customPriceFor(data, sizeId, paper);
  if (parsed.family === 'ctype') {
    const catalog = buildCatalog(data);
    const entry = catalog.get(sizeId);
    if (!entry) throw new Error(`Unknown sizeId: ${sizeId}`);
    if (paperTierOf(data, paper) !== 'ctype') throw new Error(`Paper ${paper} is not a C-Type finish`);
    return entry.price!;
  }
  // fine art
  if (isSpecialtyPaper(paper)) {
    const entry = data.productLines.fine_art.specialty.find(
      (s) => s.paper === paper && s.size === parsed.size,
    );
    if (!entry) throw new Error(`Specialty paper ${paper} not available in ${parsed.size}`);
    return entry.price;
  }
  const catalog = buildCatalog(data);
  const entry = catalog.get(sizeId);
  if (!entry) throw new Error(`Unknown sizeId: ${sizeId}`);
  const tier = paperTierOf(data, paper);
  if (tier === 'ctype') throw new Error(`Paper ${paper} is not a fine-art paper`);
  return tier === 'fineArt' ? entry.fineArt! : entry.standard!;
}

/**
 * The papers a size can actually be printed on (pricing v9):
 *  - C-Type sizes: the line's finishes, narrowed by the size's optional
 *    `papers` restriction (e.g. 8x12 prints on Lustre only);
 *  - fine-art sizes: the fine-art line's papers, plus any specialty paper
 *    stocked in that exact size;
 *  - custom (cu-) sizes: the non-specialty fine-art papers.
 * Throws for an unknown C-Type sizeId (mirrors priceFor/inchesFor).
 */
export function papersForSize(data: PricingData, sizeId: string): PaperId[] {
  const parsed = parseSizeId(sizeId);
  if (parsed.family === 'ctype') {
    for (const lineId of ['ctype_standard', 'ctype_enlargements'] as const) {
      const line = data.productLines[lineId];
      const size = line.sizes.find((s) => s.size === parsed.size);
      if (!size) continue;
      if (!size.papers?.length) return [...line.papers];
      return line.papers.filter((p) => size.papers!.includes(p));
    }
    throw new Error(`Unknown sizeId: ${sizeId}`);
  }
  const fa = data.productLines.fine_art;
  if (parsed.custom) return fa.papers.filter((p) => !isSpecialtyPaper(p));
  const out = [...fa.papers];
  for (const s of fa.specialty) {
    if (s.size === parsed.size && !out.includes(s.paper)) out.push(s.paper);
  }
  return out;
}

/**
 * Frame-config gate: can `paper` print at `printSize`? Frame-only print sizes
 * (stocked as framed configs but not loose sizes, e.g. 16x22) carry no
 * restriction data and are allowed.
 */
function paperAllowedForPrintSize(data: PricingData, paper: PaperId, printSize: string): boolean {
  const sizeId = paperTierOf(data, paper) === 'ctype' ? ctypeSizeId(printSize) : fineArtSizeId(printSize);
  try {
    return papersForSize(data, sizeId).includes(paper);
  } catch {
    return true;
  }
}

/**
 * All framed products (print + frame, one variant each), derived from the
 * config tiers. Configs whose print size can't be printed on that paper are
 * SKIPPED (per-size paper restriction — e.g. no gloss 8x12 frame).
 */
/**
 * The frame colours a pricing version offers, always at least black.
 *
 * Pricing published before 2026-07-28 has no `colours` block; it meant black,
 * so that is what it gets. Black's skuCode is '' so its ids and SKUs are
 * byte-identical to the pre-colour ones.
 */
export function frameColours(data: PricingData): Array<{ id: string; label: string; skuCode: string }> {
  const raw = data.productLines.framing.colours;
  if (!raw || Object.keys(raw).length === 0) {
    return [{ id: 'black', label: 'Black', skuCode: '' }];
  }
  return Object.entries(raw).map(([id, c]) => ({
    id,
    label: c.label,
    skuCode: c.skuCode || '',
  }));
}

/** The colour a frameId belongs to, resolved against this pricing version. */
export function frameColourFor(
  data: PricingData,
  colourCode: string,
): { id: string; label: string; skuCode: string } {
  const all = frameColours(data);
  const hit = all.find((c) => c.skuCode.toLowerCase() === (colourCode || '').toLowerCase());
  if (hit) return hit;
  // An unknown suffix is data we do not understand — fall back to the default
  // rather than throwing, so one stale id cannot take down a whole cart.
  return all.find((c) => !c.skuCode) ?? all[0]!;
}

/**
 * Physical inches for a framed config's printSize.
 *
 * sizeStringToInches() splits on "x", so an A-series name like "A3" parses to
 * [NaN] — which silently gave every A-series framed product NaN dimensions when
 * the A2 frame shipped (2026-08-28): the preview could not compute an aspect
 * ratio, so the image overflowed the frame, the 3D view rendered empty white,
 * and the in-room view drew a dot. Look the name up in the catalogue first
 * (fine art carries real A-series inches), and only fall back to parsing for
 * frame-only sizes like "16x22" that are not sold loose.
 */
export function framePrintInches(data: PricingData, printSize: string): [number, number] {
  for (const s of data.productLines.fine_art.sizes) {
    if (s.size === printSize) return s.inches;
  }
  for (const lineId of ['ctype_standard', 'ctype_enlargements'] as const) {
    for (const s of data.productLines[lineId].sizes) {
      if (s.size === printSize) return s.inches;
    }
  }
  const [w, h] = sizeStringToInches(printSize);
  if (!Number.isFinite(w) || !Number.isFinite(h)) {
    throw new Error(`Cannot resolve inches for frame print size: ${printSize}`);
  }
  return [w, h];
}

export function framedProductList(data: PricingData): FramedProduct[] {
  const f = data.productLines.framing;
  const colours = frameColours(data);
  const out: FramedProduct[] = [];
  for (const [paper, tier] of Object.entries(f.tierForPaper)) {
    for (const cfg of f.configs[tier]) {
      if (!paperAllowedForPrintSize(data, paper as PaperId, cfg.printSize)) continue;
      // Colour is an AXIS over the configs: same supplier, sizes and prices, so
      // the config rows are never duplicated per colour.
      for (const colour of colours) {
        out.push({
          frameId: makeFrameId(paper as PaperId, cfg.printSize, colour.skuCode),
          sku: frameSku(paper as PaperId, cfg.printSize, colour.skuCode),
          paper: paper as PaperId,
          family: 'frame',
          printSize: cfg.printSize,
          frameSize: cfg.frameSize,
          border: cfg.border,
          price: cfg.price,
          printInches: framePrintInches(data, cfg.printSize),
          frameInches: framePrintInches(data, cfg.frameSize),
          colour: colour.id,
          colourLabel: colour.label,
        });
      }
    }
  }
  return out;
}

/** Resolve one framed product by frameId. */
export function framedProduct(data: PricingData, frameId: string): FramedProduct {
  const { paper, printSize, colourCode } = parseFrameId(frameId);
  const colour = frameColourFor(data, colourCode);
  const f = data.productLines.framing;
  const tier = f.tierForPaper[paper];
  if (!tier) throw new Error(`Paper ${paper} cannot be framed`);
  const cfg = f.configs[tier].find((c) => c.printSize === printSize);
  if (!cfg || !paperAllowedForPrintSize(data, paper, printSize)) {
    throw new Error(`No framed config ${printSize} for paper ${paper}`);
  }
  return {
    frameId,
    sku: frameSku(paper, printSize, colour.skuCode),
    paper,
    family: 'frame',
    printSize,
    frameSize: cfg.frameSize,
    border: cfg.border,
    price: cfg.price,
    printInches: framePrintInches(data, printSize),
    frameInches: framePrintInches(data, cfg.frameSize),
    colour: colour.id,
    colourLabel: colour.label,
  };
}

/** Frame price by frameId (print included). */
export function framedPriceFor(data: PricingData, frameId: string): number {
  return framedProduct(data, frameId).price;
}

/** Per-size summed quantities of LOOSE (unframed) C-Type prints; framed items excluded from bulk. */
export function sizeQuantities(prints: PrintSelection[]): Map<string, number> {
  const sums = new Map<string, number>();
  for (const p of prints) {
    if (p.frameId) continue;
    sums.set(p.sizeId, (sums.get(p.sizeId) ?? 0) + p.qty);
  }
  return sums;
}

export interface BulkState {
  sizeId: string;
  total: number;
  threshold: number;
  active: boolean;
  remaining: number;
  price: number;
  bulkPrice: number;
  savingAtThreshold: number;
}

/** Bulk tier state per C-Type size present in the selection (drives the nudge UI). */
export function bulkState(data: PricingData, prints: PrintSelection[]): BulkState[] {
  const catalog = buildCatalog(data);
  const sums = sizeQuantities(prints);
  const out: BulkState[] = [];
  for (const [sizeId, total] of sums) {
    const entry = catalog.get(sizeId);
    if (!entry || entry.family !== 'ctype') continue;
    const threshold = entry.bulkThreshold!;
    const active = total >= threshold;
    out.push({
      sizeId,
      total,
      threshold,
      active,
      remaining: active ? 0 : threshold - total,
      price: entry.price!,
      bulkPrice: entry.bulkPrice!,
      savingAtThreshold: +(total * (entry.price! - entry.bulkPrice!)).toFixed(2),
    });
  }
  return out;
}

function cents(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * Group prints (incl. framed prints) into cart lines and compute authoritative totals.
 * One group per size+paper for loose prints, one per frameId for framed prints.
 */
export function computeTotals(data: PricingData, prints: PrintSelection[]): Totals {
  const catalog = buildCatalog(data);
  const sums = sizeQuantities(prints);
  const groups = new Map<string, CartGroup>();

  for (const p of prints) {
    if (p.qty <= 0) continue;

    // Framed print — coupled price, own cart line.
    if (p.frameId) {
      const fp = framedProduct(data, p.frameId);
      const key = groupKey(p.sizeId, p.paper, p.frameId);
      const existing = groups.get(key);
      if (existing) {
        existing.qty += p.qty;
        existing.lineTotal = cents(existing.qty * existing.effectiveUnitPrice);
      } else {
        const paperInfo = data.papers[fp.paper];
        const borderLabel = data.productLines.framing.borderStyles[fp.border];
        groups.set(key, {
          sku: fp.sku,
          sizeId: p.sizeId,
          paper: fp.paper,
          family: 'frame',
          frameId: fp.frameId,
          qty: p.qty,
          unitPrice: fp.price,
          effectiveUnitPrice: fp.price,
          bulkApplied: false,
          lineTotal: cents(p.qty * fp.price),
          displayProps: {
            Size: sizeLabel(fp.printSize, fp.printInches),
            Paper: paperInfo.label,
            Frame: `${fp.frameSize}" ${fp.colourLabel} Frame · ${borderLabel}`,
          },
        });
      }
      continue;
    }

    // Loose print.
    const parsed = parseSizeId(p.sizeId);
    const key = groupKey(p.sizeId, p.paper);
    const tier = paperTierOf(data, p.paper);
    let unit = priceFor(data, p.sizeId, p.paper);
    let effective = unit;
    let bulkApplied = false;
    let label: string;
    let sku: string;
    let family: 'ctype' | 'fine_art';
    if (parsed.custom) {
      const [w, h] = customSizeInches(p.sizeId);
      label = `Custom ${w} × ${h}"`;
      sku = customSku(tier, unit); // whole-dollar SKU, price-encoded
      family = 'fine_art';
    } else {
      const entry = catalog.get(p.sizeId);
      if (!entry) throw new Error(`Unknown sizeId: ${p.sizeId}`);
      label = entry.label;
      sku = skuFor(p.sizeId, p.paper, tier);
      family = entry.family === 'ctype' ? 'ctype' : 'fine_art';
      if (entry.family === 'ctype') {
        const total = sums.get(p.sizeId)!;
        if (total >= entry.bulkThreshold!) {
          // Bulk tier ACTIVE — switch the group to the dedicated bulk variant
          // (non-Plus model): bulk SKU, variant price == charged price.
          sku = ctypeBulkSku(entry.size);
          unit = entry.bulkPrice!;
          effective = entry.bulkPrice!;
          bulkApplied = true;
        }
      }
    }
    const existing = groups.get(key);
    if (existing) {
      existing.qty += p.qty;
      existing.lineTotal = cents(existing.qty * existing.effectiveUnitPrice);
    } else {
      const paperInfo = data.papers[p.paper];
      const displayProps: Record<string, string> =
        family === 'ctype'
          ? { Size: label, Finish: paperInfo.label }
          : { Size: label, Paper: paperInfo.label };
      groups.set(key, {
        sku,
        sizeId: p.sizeId,
        paper: p.paper,
        family,
        qty: p.qty,
        unitPrice: unit,
        effectiveUnitPrice: effective,
        bulkApplied,
        lineTotal: cents(p.qty * effective),
        displayProps,
      });
    }
  }

  const groupList = [...groups.values()];
  const subtotal = cents(groupList.reduce((acc, g) => acc + g.lineTotal, 0));
  const printCount = prints.reduce((acc, p) => acc + Math.max(0, p.qty), 0);
  const bulkApplied = bulkState(data, prints)
    .filter((b) => b.active)
    .map((b) => b.sizeId);
  return { groups: groupList, subtotal, bulkApplied, printCount };
}

/** Urgency mode: 'paid' = fee charged at checkout; 'request' = fee quoted, invoiced after staff confirm. */
export type UrgencyMode = 'paid' | 'request';

/**
 * Urgent-processing fee in CENTS for a subtotal in DOLLARS:
 * Math.round(subtotal × surchargePct) — pct 100 doubles the order
 * ($35.40 → 3540 cents). 0 when the urgency block is absent or disabled.
 * Subtotal = prints + frames (shipping never included).
 */
export function urgencyFeeCents(data: PricingData, subtotalDollars: number): number {
  const u = data.urgency;
  if (!u?.enabled) return 0;
  return Math.round(subtotalDollars * u.surchargePct);
}

/**
 * Urgency mode for a selection: 'paid' iff EVERY live print is a loose C-Type
 * (no frameId, sizeId family 'ctype' — same family rule as the free-shipping
 * eligibility check). Any fine-art, custom (cu-), or framed print → 'request'
 * (the fine-art printer runs on set days, so urgency can't be charged upfront).
 */
export function urgencyMode(prints: PrintSelection[]): UrgencyMode {
  const live = prints.filter((p) => p.qty > 0);
  const allLooseCtype = live.every((p) => !p.frameId && parseSizeId(p.sizeId).family === 'ctype');
  return allLooseCtype ? 'paid' : 'request';
}

/**
 * The bulk_tiers metafield payload for the Shopify discount Function.
 * Keyed by SKU fragment ("CT-4X6") — C-Type only. Kept in sync even though the
 * Function only runs on Shopify Plus (bulk pricing ships via bulk variants on
 * non-Plus stores); bulk SKUs ("CT-4X6-B") are intentionally NOT keys here, so
 * the Function can never double-discount a bulk-variant line.
 */
export function bulkTiersMetafield(data: PricingData): Record<string, { t: number; p: number; b: number }> {
  const out: Record<string, { t: number; p: number; b: number }> = {};
  for (const lineId of ['ctype_standard', 'ctype_enlargements'] as const) {
    const line = data.productLines[lineId];
    for (const s of line.sizes) {
      const frag = `CT-${s.size.replace(/\./g, '').toUpperCase()}`;
      out[frag] = { t: line.bulkThreshold, p: s.price, b: s.bulkPrice };
    }
  }
  return out;
}

/**
 * Resolve the border width in millimetres for a print's border mode.
 * `white` → margin within the print; `handling` → extra paper around a fine-art
 * print (fine art only); `none` → 0. Borders do NOT change price.
 * Returns { mm, geometry } where geometry is 'inset' (within print size),
 * 'outset' (added around print size), or 'none'.
 */
export function resolveBorder(
  data: PricingData,
  sizeId: string,
  mode: BorderMode,
): { mm: number; geometry: 'none' | 'inset' | 'outset' } {
  if (mode === 'none') return { mm: 0, geometry: 'none' };
  const parsed = parseSizeId(sizeId);
  const inches = inchesFor(data, sizeId);
  if (!inches) throw new Error(`Unknown sizeId for border: ${sizeId}`);
  const shortIn = Math.min(inches[0], inches[1]);
  const b = data.borders;
  if (mode === 'handling') {
    if (parsed.family !== 'fine_art') throw new Error('Handling border is fine-art only');
    return { mm: b.fineArt.handlingMm, geometry: 'outset' };
  }
  // white
  if (parsed.family === 'ctype') {
    const mm = shortIn >= b.ctype.largeThresholdInches ? b.ctype.largeMm : b.ctype.defaultMm;
    return { mm, geometry: 'inset' };
  }
  const raw = Math.round(shortIn * 25.4 * (b.fineArt.whitePercentShortEdge / 100));
  return { mm: Math.max(b.fineArt.whiteMinMm, raw), geometry: 'inset' };
}

/** In-code fallback when a published pricing version predates borders.framedMattHandlingMm. */
const FRAMED_MATT_HANDLING_MM_FALLBACK = 10;

/**
 * Is this framed configuration matted, i.e. does the print sit behind a matte
 * rather than filling the frame?
 *
 * Defined as "not printToEdge" DELIBERATELY. Three places used to compare
 * against 'doubleMatt' literally — the customer copy, the frame preview, and
 * the Worker's manifest — so adding singleMatt (owner 2026-08-28, the 20x30 and
 * A2 frames take a single matte) would have made a matted print bake BORDERLESS
 * with no tape margin behind the matte, which the framing station cannot use.
 * Every new border style is matted unless it is explicitly print-to-edge.
 */
export function isMattedBorder(border: BorderStyle): boolean {
  return border !== 'printToEdge';
}

/**
 * Handling border (mm of extra paper) baked around a framed MATTED print —
 * the tape margin behind the matte. printToEdge frames bake borderless.
 * Staff-facing only (customer previews show framed prints borderless) and
 * price-neutral like all borders.
 */
export function framedMattHandlingMm(data: PricingData): number {
  return data.borders?.framedMattHandlingMm ?? FRAMED_MATT_HANDLING_MM_FALLBACK;
}

/**
 * Can this paper's PROCESS bake a handling border (extra blank paper around the
 * print)?
 *
 * No for C-Type: the minilab prints edge to edge on the selected paper size, so
 * there is nowhere to put a margin. Only the inkjet (fine-art) papers can.
 *
 * borderModesFor() already refuses to OFFER handling on a C-Type size, and
 * printUpsert rejects it — 0 of 12,777 loose C-Type prints ever carried one.
 * The hole was the FRAMED path, which set border_mode='handling' for any matted
 * frame without checking the process, and both C-Type frame configs (5x7-in-8x10
 * and 8x12-in-12x16) are matted. Five real orders got an impossible border
 * before this landed (owner 2026-08-31). A matted C-Type print now bakes with no
 * tape margin, which the framers have confirmed they can mount.
 */
export function canBakeHandlingBorder(data: PricingData, paper: PaperId): boolean {
  return paperTierOf(data, paper) !== 'ctype';
}

/** Border modes offered for a size (framed prints get 'none' only). */
export function borderModesFor(sizeId: string, framed: boolean): BorderMode[] {
  if (framed) return ['none'];
  const parsed = parseSizeId(sizeId);
  return parsed.family === 'fine_art' ? ['none', 'white', 'handling'] : ['none', 'white'];
}

/** Minimum acceptable DPI for a size (per product family). Below this the size is blocked (override allowed). */
export function minDpiFor(data: PricingData, sizeId: string): number {
  const parsed = parseSizeId(sizeId);
  return parsed.family === 'ctype' ? data.printCheck.minDpi.ctype : data.printCheck.minDpi.fineArt;
}

/** Effective print DPI for a crop region of w×h pixels destined for a size. */
export function effectiveDpi(
  cropWidthPx: number,
  cropHeightPx: number,
  inches: [number, number],
): number {
  const [shortIn, longIn] = [Math.min(...inches), Math.max(...inches)];
  const [shortPx, longPx] = [Math.min(cropWidthPx, cropHeightPx), Math.max(cropWidthPx, cropHeightPx)];
  return Math.floor(Math.min(shortPx / shortIn, longPx / longIn));
}
